import { Fragment, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ShopItemMapper } from "./ShopComponents";
import { useShop } from "@src/contexts/ShopProvider";
import { useAuth } from "@src/contexts/AuthProvider";
import "./styles.css";
import { platform } from "@playroomkit/pr-common";
import { DisableUIOverlayEvent } from "@playroomkit/pr-wars/events";
import View from "@components/View";
import { ShopItemType, StoreSkuItem } from "@playroomkit/pr-wars-data";
import OutsideClickHandler from "@components/OutsideClickHandler/OutsideClickHandler";
import Button from "@components/Button";
import ScalableWrapper from "@components/ScalableWrapper";
import { images } from "@utils/media";

const links = [
  {
    name: "Deals",
    value: null,
  },
  {
    name: "Rounds",
    value: ShopItemType.GAME_MODE,
  },
  {
    name: "Clones",
    value: ShopItemType.BOT,
  },
  {
    name: "Podiums",
    value: ShopItemType.PODIUM,
  },
  {
    name: "Avatars",
    value: ShopItemType.AVATAR,
  },
];

const CATEGORY_NAME_MAP = {
  [ShopItemType.GAME_MODE]: "Rounds",
  [ShopItemType.BOT]: "Clones",
  [ShopItemType.AVATAR]: "Avatars",
  [ShopItemType.PODIUM]: "Podiums",
};

const getCategoryName = (type: ShopItemType): string => {
  return CATEGORY_NAME_MAP[type as keyof typeof CATEGORY_NAME_MAP] || "";
};

// Helper function to group items by category
const groupItemsByCategory = (items: any[]) => {
  const grouped: Record<string, any[]> = {};

  for (const item of items) {
    const category = item.metadata?.type;
    const categoryName = getCategoryName(category);

    if (categoryName && categoryName !== "Deals") {
      (grouped[categoryName] ||= []).push(item);
    }
  }

  return grouped;
};

const Shop = () => {
  const { userInfo } = useAuth();
  const {
    shopItems,
    filteredShopItems,
    showShop,
    selectedCategory,
    onCategoryChange,
    toggleShopVisibility,
    showPrice,
    sortAllSKUItems,
  } = useShop();

  // Scroll to top when category changes
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedCategory]);

  const handleShopClose = () => {
    toggleShopVisibility(false);
    onCategoryChange(null);
    document.dispatchEvent(new DisableUIOverlayEvent());
  };

  if (!showShop) return null;

  let SKUItems = [...filteredShopItems];
  let groupedItems: { [key: string]: any[] } = {};

  if (selectedCategory === null) {
    SKUItems = sortAllSKUItems(shopItems);
    groupedItems = groupItemsByCategory(SKUItems);
  } else {
    // For specific categories, don't show headings
    groupedItems = { "": SKUItems };
  }

  return (
    <View className="w-screen h-dvh absolute top-0 left-0 z-[999999] font-[Slackey]">
      <View
        enableEventCapturing
        className="w-full h-full absolute top-0 left-0 bg-black/60"
      />
      <OutsideClickHandler onClickOutside={handleShopClose}>
        <ScalableWrapper maintainAspectRatioBy="height">
          <View className="w-full h-full flex items-center justify-center relative">
            <View
              enableEventCapturing
              className="relative bg-[#F72EC5] w-[42rem] h-[36rem] p-1.5 rounded-xl flex flex-col items-center"
            >
              <Button
                className="bg-transparent border-none outline-none absolute -top-4 -right-4"
                onClick={handleShopClose}
              >
                <img
                  src={images.ClosePopupIcon}
                  alt="close-popup"
                  className="w-8 h-8 object-contain"
                />
              </Button>
              <div className="flex items-center gap-2">
                <img
                  src="/img/Shop/Shop_Icon.webp"
                  alt="Shop Icon"
                  className="size-7 object-contain drop-shadow-[2px_2px_0_rgba(0,0,0,0.5)]"
                />
                <img
                  src="/img/Shop/Shop_Title.webp"
                  alt="Shop"
                  className="w-20 h-14 object-contain"
                />
              </div>
              <div className="flex-1 flex flex-col bg-[url(/img/Shop/ShopBackground.webp)] size-full bg-cover bg-center font-[Slackey] rounded-b-xl relative overflow-hidden">
                {/* Header */}
                <div>
                  {/* Navbar */}
                  <div className="flex items-center gap-4 justify-center py-4 relative">
                    {links.map((link) => {
                      const isActive = selectedCategory === link.value;
                      return (
                        <View
                          key={link.name}
                          onClick={() => onCategoryChange(link.value)}
                          enableEventCapturing
                        >
                          <motion.div
                            layout
                            className={`rounded-xl p-1 inline-block relative ${
                              isActive
                                ? "bg-[#FF8000] shadow-[0_2px_0_0_#BE4F00]"
                                : "bg-[#1F1B98]"
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 17,
                            }}
                          >
                            {isActive && (
                              <motion.div
                                layoutId="activeTab"
                                className="absolute inset-0 bg-[#FF8000] rounded-xl"
                                initial={false}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 30,
                                }}
                              />
                            )}
                            <Button
                              className={`font-[Slackey] border-none outline-none text-base rounded-lg px-4 py-2 flex items-center justify-center transition-all whitespace-nowrap relative z-10 ${
                                isActive
                                  ? "text-black bg-[#FFB800]"
                                  : "text-white bg-transparent"
                              }`}
                            >
                              {link.name}
                            </Button>
                          </motion.div>
                        </View>
                      );
                    })}
                  </div>
                </div>
                {/* Shop Items - Scrollable Content */}
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-6 py-2 min-h-0"
                >
                  {!platform.isRunningInDiscord && !userInfo ? (
                    <div className="text-outline text-white text-shadow text-2xl text-center py-20">
                      You need to login to view shop
                    </div>
                  ) : (
                    <div className="mx-auto">
                      <div className="space-y-2">
                        {/* Show vip pass if present */}
                        {selectedCategory === null &&
                          shopItems.find(
                            (item) =>
                              item.metadata?.type === ShopItemType.VIP_PASS
                          ) && (
                            <div className="space-y-4">
                              <ShopItemMapper
                                item={
                                  shopItems.find(
                                    (item) =>
                                      item.metadata?.type ===
                                      ShopItemType.VIP_PASS
                                  ) as StoreSkuItem
                                }
                                showPrice={showPrice}
                              />
                            </div>
                          )}
                        {Object.entries(groupedItems).map(
                          ([categoryName, items]) => (
                            <div key={categoryName} className="space-y-4">
                              {/* Category Heading */}
                              {categoryName && (
                                <div className="flex items-center justify-center w-full">
                                  <div className="flex items-center gap-2 w-full">
                                    <div className="flex-1 border-t-3 border-b-0 border-dashed border-white"></div>
                                    <span
                                      className="bg-[#061BF6] rounded-full px-6 py-2 text-white font-[Slackey] text-center"
                                      style={{ letterSpacing: 1 }}
                                    >
                                      {categoryName}
                                    </span>
                                    <div className="flex-1 border-t-3 border-b-0 border-dashed border-white"></div>
                                  </div>
                                </div>
                              )}
                              {/* Items Grid */}
                              <div className="grid gap-6 grid-cols-3">
                                {items.map((item) => (
                                  <Fragment key={item.sku_id}>
                                    <ShopItemMapper
                                      item={item}
                                      showPrice={showPrice}
                                    />
                                  </Fragment>
                                ))}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </View>
          </View>
        </ScalableWrapper>
      </OutsideClickHandler>
    </View>
  );
};

export default Shop;
